import React, { useMemo, useEffect, useRef, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Button, CircularProgress, LinearProgress, Tooltip, Popover, TextField } from "@mui/material";
import LoopIcon from "@mui/icons-material/Loop";
import { Download, ImagePlus, Link2, Maximize2, Smile, Trash2 } from "lucide-react";
import { alpha } from "@mui/material/styles";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { markdownToHtml, htmlToMarkdown, pasteIntoMarkdownEditor } from "../utils/markdownClipboard";
import { INLINE_IMAGE_MAX_WIDTH, inlineImageCss } from "../utils/inlineImageStyle";
import { useTheme } from "../theme/useTheme";
import { ImageLightbox } from "./ImageLightbox";
import { ImageActionsToolbar } from "./ImageActionsToolbar";
import { downloadImage } from "../utils/urlHelpers";
import { useClientReadOnly } from "../hooks/useClientReadOnly";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";

const insertAt = (quill, index) => (index >= 0 ? quill.getContents(index, 1).ops[0]?.insert : undefined);

/** Whether deleting the character at `index` (`step`: -1 Backspace, 1 Delete) removes an image or joins a line to one. */
const touchesImage = (quill, index, step) => {
    const deleted = insertAt(quill, index);
    if (deleted === "\n") return Boolean(insertAt(quill, index + step)?.image);
    return Boolean(deleted?.image);
};

export function MarkdownEditor({
    value,
    onChange,
    minHeight = "200px",
    placeholder = "Escribe o edita el contenido...",
    allowImages = false,
    onImageUpload,
    maxHeight,
    resizable = false,
    initialHeight, // resizable only: starting height; minHeight stays the floor
    overlayZIndex,
    hideBorder = false,
    insertImages = true,
    onIterate,
    isIterating = false,
    iterateTooltip = "Regenerar con IA",
    iterateLabel = null,
    iteratingMessage = "Regenerando el contenido con IA…",
    readOnly: readOnlyProp = false,
    toolbarActions = null,
    attachmentContent = null,
    iterateButtonDataTutorial,
    highlightIterateButton = false,
    allowHeadings = false,
}) {
    const readOnly = useClientReadOnly() || readOnlyProp;
    const { neutrals, colors } = useTheme();
    const hasBoundedHeight =
        maxHeight !== undefined && maxHeight !== null && maxHeight !== "none";
    const resolvedHeight =
        maxHeight === true ? "100%" : hasBoundedHeight ? maxHeight : "auto";
    const [htmlContent, setHtmlContent] = useState("");
    const [uploadingImages, setUploadingImages] = useState(0);
    // Images dragged over the editor: the line marks the gap between blocks where they'll land.
    const [dropMarkerTop, setDropMarkerTop] = useState(null);
    const dropIndexRef = useRef(null);
    const wrapperRef = useRef(null);
    // Image under the pointer, with its toolbar's position in the wrapper.
    const [hoveredImage, setHoveredImage] = useState(null); // { node, top, right }
    const [zoomedImageUrl, setZoomedImageUrl] = useState("");
    const imageToolbarRef = useRef(null);
    const [anchorElEmoji, setAnchorElEmoji] = useState(null);
    // Link popover: Quill's own tooltip is clipped by the editor's overflow and does nothing without a selection.
    const [linkDialog, setLinkDialog] = useState(null); // { anchor, text, url, hasSelection }
    const linkRangeRef = useRef(null);
    const lastEmittedValue = useRef(null);
    const quillRef = useRef(null);
    const imageInputRef = useRef(null);
    const showIterateAction = typeof onIterate === "function";
    const toolbarIdRef = useRef(
        `markdown-toolbar-${Math.random().toString(36).slice(2)}`
    );
    const tooltipProps = { slotProps: { popper: { sx: { zIndex: overlayZIndex } } } };

    // Ignore only the exact value emitted by this editor, not unrelated updates.
    useEffect(() => {
        if (value === lastEmittedValue.current) return;
        setHtmlContent(markdownToHtml(value));
    }, [value]);

    const handleChange = (content, delta, source) => {
        if (readOnly) return;
        setHtmlContent(content);
        if (source !== "user") return;
        const markdown = htmlToMarkdown(content);
        lastEmittedValue.current = markdown;
        onChange(markdown);
    };

    const embedsImages = Boolean(allowImages && insertImages && onImageUpload);

    // Uploads the images in parallel and embeds them where the cursor was, in order.
    const uploadAndEmbedImages = async (files) => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        let index = quill.getSelection(true)?.index ?? quill.getLength() - 1;
        setUploadingImages(files.length);
        try {
            const imageUrls = await Promise.all(files.map(onImageUpload));
            for (const imageUrl of imageUrls.filter(Boolean)) {
                quill.insertEmbed(index, "image", imageUrl, "user");
                index += 1;
            }
            quill.setSelection(index, 0, "user");
        } finally {
            setUploadingImages(0);
        }
    };

    const imageFilesOf = (dataTransfer) =>
        Array.from(dataTransfer.files).filter((file) => file.type.startsWith("image/"));

    // Each image on its own line, before the block at `index` (the document length = after the last one).
    const uploadAndEmbedImagesAsBlocks = async (files, index) => {
        const quill = quillRef.current?.getEditor();
        setUploadingImages(files.length);
        try {
            const imageUrls = (await Promise.all(files.map(onImageUpload))).filter(Boolean);
            if (!imageUrls.length) return;
            const Delta = Quill.import("delta");
            const atEnd = index >= quill.getLength();
            const change = imageUrls.reduce(
                (delta, imageUrl) => delta.insert({ image: imageUrl }).insert("\n"),
                new Delta().retain(index)
            );
            // After the last block: an empty line below the images to keep writing.
            quill.updateContents(atEnd ? change.insert("\n") : change, "user");
            quill.setSelection(index + imageUrls.length * 2, 0, "user");
        } finally {
            setUploadingImages(0);
        }
    };

    const handleMouseMove = (event) => {
        const quill = quillRef.current?.getEditor();
        if (readOnly || !embedsImages || !quill || imageToolbarRef.current?.contains(event.target)) return;
        const image = event.target.tagName === "IMG" && quill.root.contains(event.target) ? event.target : null;
        if (image === hoveredImage?.node) return;
        if (!image) {
            setHoveredImage(null);
            return;
        }
        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const rect = image.getBoundingClientRect();
        setHoveredImage({ node: image, top: rect.top - wrapperRect.top + 8, right: wrapperRect.right - rect.right + 8 });
    };

    // Removes the image and, when it was alone on its line, the line too. The file stays: Cmd+Z brings it back.
    const removeImage = (node) => {
        const quill = quillRef.current?.getEditor();
        const index = quill.getIndex(Quill.find(node));
        const [line] = quill.getLine(index);
        const removesLine = line.length() === 2 && index + 2 < quill.getLength();
        quill.deleteText(index, removesLine ? 2 : 1, "user");
        setHoveredImage(null);
    };

    const clearDropMarker = () => {
        setDropMarkerTop(null);
        dropIndexRef.current = null;
    };

    // Items keep their type during the drag; the files themselves only arrive on drop.
    const isDraggingImages = (event) =>
        Array.from(event.dataTransfer.items || []).some((item) => item.kind === "file" && item.type.startsWith("image/"));

    // Gap closest to the pointer: before the block under it (top half) or after it (bottom half).
    const handleDragOver = (event) => {
        const quill = quillRef.current?.getEditor();
        if (readOnly || !embedsImages || !quill || !isDraggingImages(event)) return;
        event.preventDefault();
        event.stopPropagation();
        const lines = quill.getLines();
        const rects = lines.map((line) => line.domNode.getBoundingClientRect());
        let gap = rects.findIndex((rect) => event.clientY < rect.top + rect.height / 2);
        if (gap === -1) gap = lines.length;
        let markerY;
        if (gap === 0) markerY = rects[0].top;
        else if (gap === lines.length) markerY = rects[gap - 1].bottom;
        else markerY = (rects[gap - 1].bottom + rects[gap].top) / 2;
        dropIndexRef.current = gap === lines.length ? quill.getLength() : quill.getIndex(lines[gap]);
        setDropMarkerTop(markerY - wrapperRef.current.getBoundingClientRect().top);
    };

    const handleDragLeave = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) clearDropMarker();
    };

    const handleDrop = (event) => {
        const files = imageFilesOf(event.dataTransfer);
        const index = dropIndexRef.current;
        clearDropMarker();
        if (readOnly || !embedsImages || !files.length || index === null) return;
        event.preventDefault();
        event.stopPropagation();
        uploadAndEmbedImagesAsBlocks(files, index);
    };

    // Capture before Quill's native handler, which pastes through a hidden editable
    // element and lets browser formatting affect the copied lists and paragraphs.
    const handlePaste = (event) => {
        const quill = quillRef.current?.getEditor();
        if (readOnly || !quill || !quill.root.contains(event.target)) return;
        const pastedImages = imageFilesOf(event.clipboardData);
        // "Copy image" also puts an <img> in text/html; with no plain text, the file is what was copied.
        if (embedsImages && pastedImages.length && !event.clipboardData.getData("text/plain")) {
            event.preventDefault();
            uploadAndEmbedImages(pastedImages);
            return;
        }
        if (!event.clipboardData.getData("text/html") && !event.clipboardData.getData("text/plain")) return;
        event.preventDefault();
        pasteIntoMarkdownEditor(quill, event.clipboardData, Quill.import("delta"));
    };

    const handleInsertImage = useCallback(async () => {
        if (imageInputRef.current) {
            imageInputRef.current.value = "";
            imageInputRef.current.click();
        }
    }, []);

    const handleImageChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (embedsImages) {
            await uploadAndEmbedImages([file]);
            return;
        }

        let imageUrl = "";
        if (typeof onImageUpload === "function") {
            imageUrl = await onImageUpload(file);
        } else {
            imageUrl = window.prompt("Pega la URL de la imagen");
        }

        if (!imageUrl) return;

        if (!insertImages) return;
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, "image", imageUrl, "user");
        quill.setSelection(range.index + 1, 0);
    };

    const handleInsertLink = useCallback((event) => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const range = quill.getSelection(true);
        if (!range) return;
        linkRangeRef.current = range;
        setLinkDialog({
            anchor: event?.currentTarget || quill.root,
            text: range.length ? quill.getText(range.index, range.length) : "",
            url: quill.getFormat(range).link || "",
            hasSelection: range.length > 0,
        });
    }, []);

    const closeLinkDialog = () => {
        setLinkDialog(null);
        linkRangeRef.current = null;
    };

    const handleSaveLink = () => {
        const quill = quillRef.current?.getEditor();
        const range = linkRangeRef.current;
        const rawUrl = linkDialog?.url.trim();
        if (!quill || !range || !rawUrl) return;

        const url = /^(https?:\/\/|mailto:|tel:)/i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
        if (linkDialog.hasSelection) {
            quill.formatText(range.index, range.length, "link", url, "user");
            quill.setSelection(range.index + range.length, 0, "user");
        } else {
            const text = linkDialog.text.trim() || rawUrl;
            quill.insertText(range.index, text, "link", url, "user");
            quill.setSelection(range.index + text.length, 0, "user");
            quill.format("link", false, "user");
        }
        closeLinkDialog();
    };

    const handleEmojiClick = (event) => {
        setAnchorElEmoji(event.currentTarget);
    };

    const handleEmojiClose = () => {
        setAnchorElEmoji(null);
    };

    const handleEmojiSelect = (emoji) => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const range = quill.getSelection(true);
        quill.insertText(range.index, emoji.native, "user");
        quill.setSelection(range.index + emoji.native.length, 0);
        setAnchorElEmoji(null);
    };

    // Basic formatting, with optional images.
    const modules = useMemo(() => {
        if (readOnly) {
            return { toolbar: false };
        }
        return {
            clipboard: { matchVisual: false },
            // Images are removed from their toolbar only; Backspace/Delete stop next to them.
            ...(embedsImages && {
                keyboard: {
                    bindings: {
                        keepImageOnBackspace: {
                            key: 8,
                            collapsed: true,
                            handler(range) {
                                return !touchesImage(this.quill, range.index - 1, -1);
                            },
                        },
                        keepImageOnDelete: {
                            key: 46,
                            collapsed: true,
                            handler(range) {
                                return !touchesImage(this.quill, range.index, 1);
                            },
                        },
                    },
                },
            }),
            toolbar: {
                container: `#${toolbarIdRef.current}`,
                handlers: {
                    link: handleInsertLink,
                    ...(allowImages && { image: handleInsertImage }),
                },
            },
        };
    }, [allowImages, embedsImages, handleInsertImage, handleInsertLink, readOnly]);

    // Quill formats - only essential formats. Headers always, so saving keeps them.
    // Read-only keeps images so a reviewer sees the ones embedded in the text.
    const formats = [
        "header",
        "bold",
        "italic",
        "list",
        "link",
        ...(allowImages || readOnly ? ["image"] : []),
    ];

    // The caret never stays to the right of an image: it goes to the line below (created if missing), as in
    // Notion. Coming back from that line (←, ↑) it jumps to the line above the image instead.
    useEffect(() => {
        const quill = quillRef.current?.getEditor();
        if (!quill || readOnly) return undefined;
        const handleSelectionChange = (range, oldRange) => {
            if (!range || range.length || !insertAt(quill, range.index - 1)?.image || insertAt(quill, range.index) !== "\n") return;
            if (oldRange?.index === range.index + 1) {
                quill.setSelection(Math.max(range.index - 2, 0), 0, "user");
                return;
            }
            if (range.index + 1 >= quill.getLength()) quill.insertText(range.index, "\n", "user");
            quill.setSelection(range.index + 1, 0, "user");
        };
        quill.on("selection-change", handleSelectionChange);
        return () => quill.off("selection-change", handleSelectionChange);
    }, [readOnly]);

    // Watch for new links and auto-fix URLs without protocol
    useEffect(() => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const fixLinkUrls = () => {
            const editor = quill.root;
            const links = editor.querySelectorAll('a');
            links.forEach(link => {
                const href = link.getAttribute('href');
                if (href && !href.match(/^[a-zA-Z]+:\/\//) && !href.startsWith('mailto:') && !href.startsWith('#')) {
                    // Auto-add https:// if no protocol
                    const newHref = 'https://' + href;
                    link.setAttribute('href', newHref);

                    // Find the correct position of this link using Quill's scroll
                    try {
                        const Parchment = quill.scroll.constructor;
                        const blot = Parchment.find(link) || quill.scroll.find(link);
                        if (blot && typeof quill.getIndex === 'function') {
                            const index = quill.getIndex(blot);
                            const length = link.textContent.length;
                            quill.formatText(index, length, 'link', newHref, 'silent');
                        }
                    } catch (e) {
                        // Fallback: just update the DOM attribute (already done above)
                        console.debug('Could not update Quill delta for link:', e);
                    }
                }
            });
        };

        // Use MutationObserver to detect changes in the editor
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' || mutation.type === 'attributes') {
                    // Check if a link was added or modified
                    const hasLinkChange = Array.from(mutation.addedNodes).some(node =>
                        node.nodeName === 'A' || (node.querySelector && node.querySelector('a'))
                    ) || (mutation.attributeName === 'href');

                    if (hasLinkChange || mutation.target.nodeName === 'A') {
                        setTimeout(fixLinkUrls, 50);
                    }
                }
            });
        });

        observer.observe(quill.root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['href']
        });

        return () => observer.disconnect();
    }, [htmlContent]);

    return (
        <div
            ref={wrapperRef}
            id={`${toolbarIdRef.current}-editor`}
            onPasteCapture={handlePaste}
            onDragOverCapture={handleDragOver}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoveredImage(null)}
            onScrollCapture={() => setHoveredImage(null)}
            onDragLeave={handleDragLeave}
            onDropCapture={handleDrop}
            style={{
                width: "100%",
                height: resolvedHeight,
                border: hideBorder ? "none" : `1px solid ${neutrals.gray[200]}`,
                borderRadius: hideBorder ? 0 : "8px",
                overflow: hideBorder ? "visible" : "hidden",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                ...(resizable && {
                    resize: "vertical",
                    overflow: "hidden",
                    height: `calc(${initialHeight || minHeight} + 42px)`,
                    minHeight: `calc(${minHeight} + 42px)`,
                }),
            }}
        >
            {!readOnly && (
                <div id={toolbarIdRef.current} className="ql-toolbar ql-snow" style={{ flexShrink: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", width: "100%" }}>
                        <span className="ql-formats" style={{ display: "flex", alignItems: "center", gap: "4px", flex: 1 }}>
                            {allowHeadings && (
                                <>
                                    <Tooltip {...tooltipProps} title="Encabezado H2" arrow>
                                        <button type="button" className="ql-header" value="2" aria-label="Encabezado H2" />
                                    </Tooltip>
                                    <Tooltip {...tooltipProps} title="Encabezado H3" arrow>
                                        <button type="button" className="ql-header ql-header-text" value="3" aria-label="Encabezado H3">
                                            H3
                                        </button>
                                    </Tooltip>
                                </>
                            )}
                            <Tooltip {...tooltipProps} title="Texto en negrita" arrow>
                                <button type="button" className="ql-bold" aria-label="Texto en negrita" />
                            </Tooltip>
                            <Tooltip {...tooltipProps} title="Texto en cursiva" arrow>
                                <button type="button" className="ql-italic" aria-label="Texto en cursiva" />
                            </Tooltip>
                            <Tooltip {...tooltipProps} title="Lista con viñetas" arrow>
                                <button type="button" className="ql-list" value="bullet" aria-label="Lista con viñetas" />
                            </Tooltip>
                            <Tooltip {...tooltipProps} title="Lista numerada" arrow>
                                <button type="button" className="ql-list" value="ordered" aria-label="Lista numerada" />
                            </Tooltip>
                            <Tooltip {...tooltipProps} title="Añadir enlace en texto" arrow>
                                <button
                                    type="button"
                                    className="ql-custom-link"
                                    aria-label="Añadir enlace en texto"
                                    onClick={handleInsertLink}
                                    style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                    <Link2 size={16} />
                                </button>
                            </Tooltip>
                            <Tooltip {...tooltipProps} title="Añadir emoji" arrow>
                                <button
                                    type="button"
                                    className="ql-custom-emoji"
                                    aria-label="Añadir emoji"
                                    onClick={handleEmojiClick}
                                    style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                    <Smile size={16} />
                                </button>
                            </Tooltip>
                            {allowImages && !embedsImages && (
                                <Tooltip {...tooltipProps} title="Añadir imagen" arrow>
                                    <button type="button" className="ql-image" />
                                </Tooltip>
                            )}
                            {showIterateAction && (
                                <span
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        marginLeft: "12px",
                                        paddingLeft: "12px",
                                        borderLeft: `1px solid ${neutrals.gray[200]}`,
                                    }}
                                >
                                    <Tooltip {...tooltipProps} title={iterateTooltip} arrow>
                                        <button
                                            type="button"
                                            className="ql-iterate"
                                            onClick={(event) => {
                                                // Not left focused: the dialog would hand focus back and keep it blue
                                                event.currentTarget.blur();
                                                onIterate(event);
                                            }}
                                            disabled={isIterating}
                                            data-tutorial={iterateButtonDataTutorial}
                                            data-highlight-iterate={highlightIterateButton ? "true" : undefined}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                ...(iterateLabel ? { width: "auto", gap: 6, padding: "0 10px", fontSize: 13, fontWeight: 600 } : {}),
                                            }}
                                        >
                                            <LoopIcon sx={{ fontSize: 16 }} />
                                            {iterateLabel}
                                        </button>
                                    </Tooltip>
                                </span>
                            )}
                        </span>
                        {(toolbarActions || embedsImages) && (
                            <div className="markdown-toolbar-actions" style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "auto", minWidth: 0 }}>
                                {embedsImages && (
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            padding: "4px 10px",
                                            borderRadius: 999,
                                            border: `1px dashed ${alpha(colors.primary.main, 0.45)}`,
                                            backgroundColor: alpha(colors.primary.main, 0.08),
                                            color: colors.primary.main,
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        <ImagePlus size={14} />
                                        Arrastra o pega imágenes en el texto
                                    </span>
                                )}
                                {toolbarActions}
                            </div>
                        )}
                    </div>
                </div>
            )}
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={htmlContent}
                onChange={handleChange}
                modules={modules}
                formats={formats}
                placeholder={placeholder}
                readOnly={readOnly}
                style={{
                    minHeight: hasBoundedHeight ? "0" : minHeight,
                    flex: hasBoundedHeight ? 1 : "auto",
                    display: hasBoundedHeight ? "flex" : "block",
                    flexDirection: hasBoundedHeight ? "column" : "initial",
                    fontFamily: "inherit",
                    fontSize: "0.85rem",
                    overflow: hasBoundedHeight ? "hidden" : "visible",
                }}
            />
            {hoveredImage && (
                <ImageActionsToolbar
                    ref={imageToolbarRef}
                    tooltipProps={tooltipProps}
                    sx={{ position: "absolute", top: hoveredImage.top, right: hoveredImage.right }}
                    actions={[
                        { label: "Ampliar", Icon: Maximize2, onClick: () => setZoomedImageUrl(hoveredImage.node.src) },
                        { label: "Descargar", Icon: Download, onClick: () => downloadImage(hoveredImage.node.src) },
                        { label: "Eliminar", Icon: Trash2, onClick: () => removeImage(hoveredImage.node) },
                    ]}
                />
            )}
            {zoomedImageUrl && <ImageLightbox url={zoomedImageUrl} onClose={() => setZoomedImageUrl("")} />}
            {dropMarkerTop !== null && (
                <>
                    <div
                        aria-hidden
                        style={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "inherit",
                            boxShadow: `inset 0 0 0 2px ${colors.primary.main}`,
                            backgroundColor: alpha(colors.primary.main, 0.05),
                            pointerEvents: "none",
                            zIndex: 3,
                        }}
                    />
                    <div
                        aria-hidden
                        style={{
                            position: "absolute",
                            left: 12,
                            right: 12,
                            top: dropMarkerTop - 2,
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: colors.primary.main,
                            pointerEvents: "none",
                            zIndex: 3,
                        }}
                    />
                </>
            )}
            {uploadingImages > 0 && (
                <div
                    role="status"
                    aria-live="polite"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "6px 12px",
                        fontSize: 12,
                        color: neutrals.gray[600],
                        borderTop: `1px solid ${neutrals.gray[200]}`,
                        flexShrink: 0,
                    }}
                >
                    <CircularProgress size={12} />
                    {uploadingImages > 1 ? `Subiendo ${uploadingImages} imágenes…` : "Subiendo imagen…"}
                </div>
            )}
            {attachmentContent && (
                <div
                    className="markdown-attachments"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px 12px",
                        borderTop: `1px solid ${neutrals.gray[200]}`,
                        backgroundColor: neutrals.gray[50],
                        flexShrink: 0,
                    }}
                >
                    {attachmentContent}
                </div>
            )}
            {isIterating && (
                <div
                    role="status"
                    aria-live="polite"
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.78)",
                        backdropFilter: "blur(1.5px)",
                        zIndex: 2,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 10,
                            width: "min(320px, 80%)",
                            padding: "20px 24px",
                            borderRadius: 14,
                            backgroundColor: "#fff",
                            border: `1px solid ${neutrals.gray[200]}`,
                            boxShadow: "0 8px 24px rgba(16, 24, 40, 0.12)",
                            textAlign: "center",
                        }}
                    >
                        <CircularProgress size={26} />
                        <div style={{ fontSize: 14, fontWeight: 600, color: neutrals.gray[800] }}>
                            {iteratingMessage}
                        </div>
                        <div style={{ fontSize: 12, color: neutrals.gray[500] }}>
                            Puede tardar hasta un minuto. No cierres esta pantalla.
                        </div>
                        <LinearProgress sx={{ width: "100%", borderRadius: 4 }} />
                    </div>
                </div>
            )}
            {allowImages && (
                <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                />
            )}
            <Popover
                open={Boolean(linkDialog)}
                anchorEl={linkDialog?.anchor}
                onClose={closeLinkDialog}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                    zIndex: overlayZIndex,
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        marginTop: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    }
                }}
            >
                {linkDialog && (
                    <form
                        onSubmit={(event) => {
                            event.preventDefault();
                            handleSaveLink();
                        }}
                        style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "14px", width: "300px" }}
                    >
                        {!linkDialog.hasSelection && (
                            <TextField
                                size="small"
                                label="Texto"
                                placeholder="Texto del enlace (opcional)"
                                value={linkDialog.text}
                                onChange={(event) => setLinkDialog((prev) => ({ ...prev, text: event.target.value }))}
                            />
                        )}
                        <TextField
                            size="small"
                            label="URL"
                            placeholder="https://..."
                            autoFocus
                            value={linkDialog.url}
                            onChange={(event) => setLinkDialog((prev) => ({ ...prev, url: event.target.value }))}
                        />
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                            <Button size="small" onClick={closeLinkDialog} sx={{ textTransform: "none" }}>
                                Cancelar
                            </Button>
                            <Button
                                size="small"
                                type="submit"
                                variant="contained"
                                disabled={!linkDialog.url.trim()}
                                sx={{ textTransform: "none", boxShadow: "none" }}
                            >
                                Insertar
                            </Button>
                        </div>
                    </form>
                )}
            </Popover>
            <Popover
                open={Boolean(anchorElEmoji)}
                anchorEl={anchorElEmoji}
                onClose={handleEmojiClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    zIndex: overlayZIndex,
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        marginTop: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        maxHeight: '300px',
                    }
                }}
            >
                <Picker
                    data={data}
                    onEmojiSelect={handleEmojiSelect}
                    theme="light"
                    locale="es"
                    set="native"
                    previewPosition="none"
                    perLine={8}
                    emojiSize={20}
                    emojiButtonSize={30}
                />
            </Popover>
            <style jsx global>{`
                ${resizable ? `
                #${toolbarIdRef.current}-editor > .quill {
                    flex: 1;
                    min-height: 0 !important;
                    display: flex !important;
                    flex-direction: column;
                    overflow: hidden !important;
                }
                #${toolbarIdRef.current}-editor .ql-container {
                    flex: 1;
                    min-height: 0;
                    overflow: hidden;
                }
                #${toolbarIdRef.current}-editor .ql-editor {
                    height: 100%;
                    min-height: 0;
                    overflow-y: auto !important;
                }
                ` : ""}
                .ql-toolbar {
                    background-color: ${neutrals.gray[50]} !important;
                    border: none !important;
                    border-bottom: 1px solid ${neutrals.gray[200]} !important;
                }
                .ql-toolbar button.ql-iterate svg {
                    width: 16px;
                    height: 16px;
                }
                /* Quill paints a focused toolbar button blue; the dialog hands focus back to this one */
                .ql-toolbar.ql-snow button.ql-iterate,
                .ql-toolbar.ql-snow button.ql-iterate:focus,
                .ql-toolbar.ql-snow button.ql-iterate:active {
                    color: ${neutrals.gray[600]} !important;
                }
                .ql-toolbar.ql-snow button.ql-iterate:hover {
                    color: ${neutrals.gray[800]} !important;
                }
                .ql-toolbar button[data-highlight-iterate="true"] {
                    position: relative;
                }
                .ql-toolbar button[data-highlight-iterate="true"]::after {
                    content: "";
                    position: absolute;
                    width: 24px;
                    height: 24px;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    border-radius: 50%;
                    border: 1px solid ${colors.error.main};
                    pointer-events: none;
                }
                /* Style custom link button */
                .ql-toolbar button.ql-custom-link {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 3px 5px;
                    width: 28px;
                    height: 24px;
                }
                .ql-toolbar button.ql-custom-link svg,
                .ql-toolbar button.ql-custom-emoji svg {
                    stroke: ${neutrals.gray[600]};
                }
                .ql-toolbar button.ql-custom-link:hover svg,
                .ql-toolbar button.ql-custom-emoji:hover svg {
                    stroke: ${neutrals.gray[800]};
                }
                .ql-toolbar button.ql-custom-emoji {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 3px 5px;
                    width: 28px;
                    height: 24px;
                }
                .ql-container {
                    border: none !important;
                    font-size: 0.85rem !important;
                    font-family: inherit !important;
                    position: relative !important;
                }
                .ql-editor {
                    padding: 12px;
                    line-height: 1.6;
                }
                /* Size rules depend on this editor's props: scoped so several editors don't overwrite each other */
                #${toolbarIdRef.current}-editor .ql-container {
                    ${hasBoundedHeight ? `flex: 1;` : ""}
                    ${hasBoundedHeight ? `display: flex;` : ""}
                    ${hasBoundedHeight ? `flex-direction: column;` : ""}
                    ${hasBoundedHeight ? `overflow: hidden;` : ""}
                }
                #${toolbarIdRef.current}-editor .ql-editor {
                    min-height: ${resizable ? 0 : minHeight};
                    ${hasBoundedHeight ? `flex: 1;` : ""}
                    ${hasBoundedHeight ? "overflow-y: auto !important;" : ""}
                }
                .ql-editor p {
                    min-height: 1.6em;
                }
                /* Beats quill.snow's ".ql-snow .ql-editor img { max-width: 100% }" */
                #${toolbarIdRef.current}-editor .ql-editor img {
                    ${inlineImageCss(INLINE_IMAGE_MAX_WIDTH)}
                    margin: 0.5em 0;
                }
                .ql-editor h1, .ql-editor h2, .ql-editor h3 {
                    line-height: 1.3;
                    margin: 0.6em 0 0.25em;
                }
                .ql-editor h1 { font-size: 1.35em; }
                .ql-editor h2 { font-size: 1.2em; }
                .ql-editor h3 { font-size: 1.05em; }
                .ql-toolbar button.ql-header-text {
                    font-size: 12px;
                    font-weight: 700;
                    color: ${neutrals.gray[600]};
                }
                .ql-toolbar button.ql-header-text.ql-active {
                    color: ${colors.primary?.main || neutrals.gray[900]};
                }
                .ql-editor p:empty::before {
                    content: "\\00a0";
                }
                .ql-editor.ql-blank::before {
                    color: ${neutrals.gray[400]};
                    font-style: normal;
                }
                .ql-snow .ql-stroke {
                    stroke: ${neutrals.gray[600]} !important;
                }
                .ql-snow .ql-fill {
                    fill: ${neutrals.gray[600]} !important;
                }
                .ql-snow .ql-picker-label {
                    color: ${neutrals.gray[600]} !important;
                }
                .ql-toolbar.ql-snow .ql-picker-label:hover,
                .ql-toolbar.ql-snow button:hover {
                    color: ${neutrals.gray[800]} !important;
                }
                .ql-toolbar.ql-snow .ql-picker-label:hover .ql-stroke,
                .ql-toolbar.ql-snow button:hover .ql-stroke {
                    stroke: ${neutrals.gray[800]} !important;
                }
                .ql-toolbar.ql-snow .ql-picker-label:hover .ql-fill,
                .ql-toolbar.ql-snow button:hover .ql-fill {
                    fill: ${neutrals.gray[800]} !important;
                }
                /* Quill's link tooltip styling */
                .ql-snow .ql-tooltip {
                    position: absolute !important;
                    z-index: 9999 !important;
                    background-color: white !important;
                    border: 1px solid ${neutrals.gray[300]} !important;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
                    padding: 8px 12px !important;
                    border-radius: 6px !important;
                    left: 50% !important;
                    transform: translateX(-50%) !important;
                    margin-top: 10px !important;
                }
                .ql-snow .ql-tooltip input[type=text] {
                    border: 1px solid ${neutrals.gray[300]} !important;
                    padding: 6px 8px !important;
                    border-radius: 4px !important;
                    outline: none !important;
                    font-size: 0.875rem !important;
                }
                .ql-snow .ql-tooltip input[type=text]:focus {
                    border-color: #1976d2 !important;
                }
                .ql-snow .ql-tooltip a.ql-action,
                .ql-snow .ql-tooltip a.ql-remove {
                    margin-left: 8px !important;
                }
            `}</style>

        </div>
    );
}

MarkdownEditor.propTypes = {
    resizable: PropTypes.bool,
    initialHeight: PropTypes.string,
    overlayZIndex: PropTypes.number,
    allowHeadings: PropTypes.bool,
};
